<script setup>
import { nextTick, ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();

const isOpen = ref(false);
const contactsFormRef = ref(null);
const contact = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);
const isFormInvalid = computed(
  () => contactsFormRef.value?.isFormInvalid ?? false
);

const createNewContact = contactItem => {
  contact.value = contactItem;
};

const handleDialogConfirm = async () => {
  if (!contact.value || isCreatingContact.value || isFormInvalid.value) return;
  emit('create', contact.value);
};

const close = () => {
  if (isCreatingContact.value) return;
  isOpen.value = false;
  contact.value = null;
};

const onSuccess = () => {
  contactsFormRef.value?.resetForm();
  contact.value = null;
  close();
};

const open = async () => {
  contact.value = null;
  isOpen.value = true;
  await nextTick();
  contactsFormRef.value?.resetForm();
  contactsFormRef.value?.resetValidation();
};

const dialogRef = { open, close };

defineExpose({ dialogRef, contactsFormRef, onSuccess });
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[80] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="
        isOpen
          ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
          : 'bg-transparent'
      "
      @click.self="close"
    >
      <div
        class="flex h-[88vh] max-h-[88vh] w-full flex-col overflow-hidden rounded-t-[1.5rem] bg-n-solid-2 shadow-xl transition-transform duration-200 ease-in-out sm:max-w-xl sm:rounded-t-2xl"
        :class="isOpen ? 'translate-y-0' : 'translate-y-full'"
      >
        <div class="flex items-center gap-3 bg-n-solid-1 px-4 py-3">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            :aria-label="t('GENERAL.CLOSE')"
            :disabled="isCreatingContact"
            @click="close"
          />
          <h3 class="truncate text-base font-medium text-n-slate-12">
            {{
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.CREATE_TITLE')
            }}
          </h3>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <ContactsForm
            ref="contactsFormRef"
            is-new-contact
            sheet-presentation
            :show-bio="false"
            :show-social-links="false"
            :show-edit-section-title="false"
            @update="createNewContact"
          />
        </div>

        <div
          class="flex flex-col gap-2 border-t border-n-strong bg-n-solid-1 px-4 pb-4 pt-3"
        >
          <div class="flex gap-2">
            <Button
              variant="outline"
              color="slate"
              size="md"
              class="flex-1"
              :label="
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.CANCEL')
              "
              :disabled="isCreatingContact"
              @click="close"
            />
            <Button
              variant="solid"
              color="blue"
              size="md"
              class="flex-1"
              :label="
                isCreatingContact
                  ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVING')
                  : t(
                      'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT'
                    )
              "
              :disabled="isCreatingContact || isFormInvalid"
              @click="handleDialogConfirm"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
